import { Helmet } from "react-helmet-async";

/**
 * Injects a JSON-LD structured-data block into the document <head>.
 *
 * Usage:
 *   <StructuredData data={LOCAL_BUSINESS_SCHEMA} />
 *
 * The `data` object is serialized exactly as-is — caller is responsible
 * for following schema.org's vocabulary. Multiple <StructuredData> tags
 * may render on the same route (e.g. LocalBusiness + BreadcrumbList).
 */
export default function StructuredData({ data }) {
    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(data)}
            </script>
        </Helmet>
    );
}

const SITE_URL = "https://actluxe.net";
const PHONE = "+1-803-728-9097";

// Shared Organization node inlined in every Service / CreativeWork schema.
// Keeping this in one place means the org identity stays in sync across
// every emitted schema block.
const PROVIDER_ORG = {
    "@type": "Organization",
    name: "Advanced Connected Technologies",
    url: `${SITE_URL}/`,
    telephone: PHONE,
};

/**
 * LocalBusiness schema for Advanced Connected Technologies. Mounted on
 * `/` and `/contact`. Points at the production domain (https://actluxe.net/).
 */
export const ACT_LOCAL_BUSINESS_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    additionalType: "https://schema.org/ElectricalContractor",
    name: "Advanced Connected Technologies",
    url: "https://actluxe.net/",
    logo: "https://cdn.imageurlgenerator.com/uploads/9fcf44d0-34fa-4cee-a148-c22a70262099.jpeg",
    image: "https://cdn.imageurlgenerator.com/uploads/9fcf44d0-34fa-4cee-a148-c22a70262099.jpeg",
    telephone: "+1-803-728-9097",
    email: "concierge@actluxe.net",
    foundingDate: "2026",
    address: {
        "@type": "PostalAddress",
        addressRegion: "Eastern United States",
        addressCountry: "US",
    },
    areaServed: [{ "@type": "Country", name: "United States" }],
    openingHoursSpecification: [
        {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
            ],
            opens: "00:00",
            closes: "23:59",
        },
    ],
    description:
        "Luxury smart home integration: home automation, security, audio/video, and networking for high-end residences. Certified Control4 and Savant integrators serving the Eastern United States nationally.",
    priceRange: "$$$$",
};

/**
 * Trim a paragraph to a target character length at the nearest word
 * boundary. Adds an ellipsis if trimmed.
 */
function trimTo(str, max = 200) {
    if (!str) return "";
    const s = String(str).replace(/\s+/g, " ").trim();
    if (s.length <= max) return s;
    const cut = s.slice(0, max);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim() + "…";
}

/**
 * Service schema for `/services/{slug}` detail pages. Pulls the human-
 * readable name and intro paragraph from the shared `SERVICES` data — the
 * caller passes the whole service object.
 */
export function serviceSchema(service) {
    return {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: service.name,
        provider: PROVIDER_ORG,
        areaServed: [{ "@type": "Country", name: "United States" }],
        description: trimTo(service.intro, 220),
        url: `${SITE_URL}/services/${service.slug}`,
    };
}
